// Wiki: https://outlookweb.visualstudio.com/Outlook%20Web/_wiki/wikis/Outlook%20Web.wiki/23614/ComplianceId-for-Sent-Item-Protection-Rights-Management
import type RightsManagementLicenseDataType from 'owa-service/lib/contract/RightsManagementLicenseDataType';
import type ProtectionViewState from 'owa-mail-protection-types/lib/schema/ProtectionViewState';
import type { MailboxInfo } from 'owa-client-types';
import {
    ENCRYPT_DNF_GUID,
    ENCRYPT_GUID,
} from 'owa-protection-store/lib/store/schema/rms/EncryptionGUID';
import { isAccountFeatureEnabled } from 'owa-feature-flags';
import { LabelApplyMethod } from 'owa-mail-protection-types/lib/schema/clp/CLPViewState';

// For both New Compose Scenario and Reply/Forward Scenario
export default function getComplianceId(
    protectionViewState?: ProtectionViewState,
    mailboxInfo?: MailboxInfo
): string | undefined {
    const IRMData: RightsManagementLicenseDataType | undefined = protectionViewState?.IRMData;
    const userHasOwnerOrEditExportRights =
        mailboxInfo && isAccountFeatureEnabled('snc-clp-encrypt-sentItems', mailboxInfo)
            ? hasOwnerOrEditExportRights(IRMData)
            : false;
    // If the user doesn't have rights then don't set complianceId.
    if (!userHasOwnerOrEditExportRights) {
        return undefined;
    }

    const selectedCLPLabel = protectionViewState?.clpViewState?.selectedCLPLabel;
    const originalCLPLabel = protectionViewState?.clpViewState?.originalCLPLabel;
    // S/MIME level protection is already applied. Setting ComplianceId would also apply RMS.
    if (selectedCLPLabel?.isSmimeEncrypt || selectedCLPLabel?.isSmimeSign) {
        return IRMData || originalCLPLabel?.isEncryptingLabel ? '0' : undefined;
    }

    let complianceId: string | undefined;
    const IRMChanged = protectionViewState?.IRMChanged;

    if (IRMChanged) {
        // IRMChanged = true implies,
        // 1. IRM Protection is applied
        // 2. IRM Protection is removed
        // 3. IRM Protection is switched (ex: EncryptOnly to DoNotForward)
        // 4. Change from non-encrypting to encrypting label
        // 5. Change from one encrypting to another encrypting label
        if (selectedCLPLabel?.isEncryptingLabel) {
            if (selectedCLPLabel.rmsTemplateId) {
                complianceId = selectedCLPLabel.rmsTemplateId;
            } else if (selectedCLPLabel.rightsProtectMessageDNF) {
                complianceId = ENCRYPT_DNF_GUID;
            } else if (selectedCLPLabel.rightsProtectMessageEncryptOnly) {
                complianceId = ENCRYPT_GUID;
            }
        } else if (IRMData?.RmsTemplateId) {
            complianceId = IRMData.RmsTemplateId;
        } else {
            complianceId = '0';
        }
    } else if (
        protectionViewState?.clpViewState?.labelChanged &&
        originalCLPLabel?.isEncryptingLabel &&
        !selectedCLPLabel?.isEncryptingLabel
    ) {
        // IRMChanged = false and labelChanged = true implies a non-encrypting label is applied.
        // Here we have 2 cases:
        // 1. If the original label is encrypting and the selected label is non-encrypting, then we need to remove the protection (set complianceId to '0').
        // 2. If both original and selected labels are non-encrypting, then no change in complianceId.
        // The condition above (originalCLPLabel?.isEncryptingLabel && !selectedCLPLabel?.isEncryptingLabel) ensures we only enter this block for case 1.
        complianceId = '0';
    } else if (
        protectionViewState?.clpViewState?.labelApplyMethod === LabelApplyMethod.Default &&
        protectionViewState?.clpViewState?.labelChanged &&
        selectedCLPLabel?.isEncryptingLabel
    ) {
        // Encrypting label is applied via default label policy.
        if (selectedCLPLabel?.rmsTemplateId) {
            complianceId = selectedCLPLabel.rmsTemplateId;
        } else if (selectedCLPLabel?.rightsProtectMessageDNF) {
            complianceId = ENCRYPT_DNF_GUID;
        } else if (selectedCLPLabel?.rightsProtectMessageEncryptOnly) {
            complianceId = ENCRYPT_GUID;
        }
    }

    return complianceId;
}

export function hasOwnerOrEditExportRights(
    IRMData: RightsManagementLicenseDataType | undefined
): boolean {
    // If there is no IRM data, then we can assume that the user has rights (Fresh Compose Scenario).
    return !IRMData || !!IRMData.IsOwner || !!(IRMData.EditAllowed && IRMData.ExportAllowed);
}
